import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getMailchimpCampaigns,
  getMailchimpDailyGrowth,
  getMailchimpEngagement,
  getMailchimpGrowth,
  getMailchimpHwSaasSplit,
} from '../api/mailchimpAnalytics';
import type {
  MailchimpCampaigns,
  MailchimpDailyGrowth,
  MailchimpEngagement,
  MailchimpGrowth,
  MailchimpHwSaasSplit,
  MailchimpResponseMeta,
} from '../types/mailchimp';
import styles from './EmailMarketing.module.css';

/**
 * Email Marketing page.
 *
 * Reads aggregate, anonymous analytics from /api/admin/mailchimp/* and
 * shows daily list activity, longer-term growth, hardware-vs-SaaS intent,
 * and a recent-campaigns table. Member-level PII is never fetched.
 */

const GROWTH_WINDOWS = [
  { label: '6m', months: 6 },
  { label: '1y', months: 12 },
  { label: '2y', months: 24 },
];

const DAILY_WINDOWS = [
  { label: '30d', days: 30 },
  { label: '60d', days: 60 },
  { label: '90d', days: 90 },
];

const HW_SAAS_COLORS = {
  device_only: '#3b82f6',
  saas_only: '#a855f7',
  both: '#22c55e',
  neither: '#6b7280',
};

const TOOLTIP_STYLE = {
  background: '#0d0f14',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  fontSize: '0.8125rem',
} as const;

function fmtNumber(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

function fmtPercent(rate: number | null | undefined, digits = 1): string {
  if (rate == null || Number.isNaN(rate)) return '—';
  // Mailchimp report rates come back as decimals (0.32 = 32%)
  return `${(rate * 100).toFixed(digits)}%`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtMonthShort(month: string): string {
  // expects "YYYY-MM"
  const [y, m] = month.split('-');
  if (!y || !m) return month;
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

function fmtDayShort(iso: string): string {
  // expects "YYYY-MM-DD"
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function fmtSigned(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  const formatted = Math.abs(n).toLocaleString();
  if (n > 0) return `+${formatted}`;
  if (n < 0) return `−${formatted}`;
  return '0';
}

export default function EmailMarketing() {
  const [growthMonths, setGrowthMonths] = useState<number>(12);
  const [dailyDays, setDailyDays] = useState<number>(60);

  const [growth, setGrowth] = useState<MailchimpGrowth | null>(null);
  const [dailyGrowth, setDailyGrowth] = useState<MailchimpDailyGrowth | null>(
    null
  );
  const [hwSaas, setHwSaas] = useState<MailchimpHwSaasSplit | null>(null);
  const [engagement, setEngagement] = useState<MailchimpEngagement | null>(null);
  const [campaigns, setCampaigns] = useState<MailchimpCampaigns | null>(null);

  const [loading, setLoading] = useState({
    growth: true,
    dailyGrowth: true,
    hwSaas: true,
    engagement: true,
    campaigns: true,
  });
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [refreshing, setRefreshing] = useState(false);

  // The backend wraps every endpoint in a cache layer that, on Mailchimp
  // fetch failure, returns a response carrying `error` (and `stale: true`
  // when a previously-cached value was still around). Surface that error
  // unless the response is stale — stale responses still have real data
  // we want to keep showing.
  function errorFromResponse(
    resp: MailchimpResponseMeta | null | undefined
  ): string | null {
    if (resp?.error && !resp.stale) return `Mailchimp: ${resp.error}`;
    return null;
  }

  const loadGrowth = useCallback(
    async (refresh = false) => {
      setLoading((l) => ({ ...l, growth: true }));
      setErrors((e) => ({ ...e, growth: null }));
      try {
        const data = await getMailchimpGrowth(growthMonths, { refresh });
        setGrowth(data);
        setErrors((e) => ({ ...e, growth: errorFromResponse(data) }));
      } catch {
        setErrors((e) => ({ ...e, growth: 'Failed to load growth' }));
      } finally {
        setLoading((l) => ({ ...l, growth: false }));
      }
    },
    [growthMonths]
  );

  const loadDailyGrowth = useCallback(
    async (refresh = false) => {
      setLoading((l) => ({ ...l, dailyGrowth: true }));
      setErrors((e) => ({ ...e, dailyGrowth: null }));
      try {
        const data = await getMailchimpDailyGrowth(dailyDays, { refresh });
        setDailyGrowth(data);
        setErrors((e) => ({ ...e, dailyGrowth: errorFromResponse(data) }));
      } catch {
        setErrors((e) => ({
          ...e,
          dailyGrowth: 'Failed to load daily activity',
        }));
      } finally {
        setLoading((l) => ({ ...l, dailyGrowth: false }));
      }
    },
    [dailyDays]
  );

  const loadHwSaas = useCallback(async (refresh = false) => {
    setLoading((l) => ({ ...l, hwSaas: true }));
    setErrors((e) => ({ ...e, hwSaas: null }));
    try {
      const data = await getMailchimpHwSaasSplit({ refresh });
      setHwSaas(data);
      setErrors((e) => ({ ...e, hwSaas: errorFromResponse(data) }));
    } catch {
      setErrors((e) => ({ ...e, hwSaas: 'Failed to load split' }));
    } finally {
      setLoading((l) => ({ ...l, hwSaas: false }));
    }
  }, []);

  const loadEngagement = useCallback(async (refresh = false) => {
    setLoading((l) => ({ ...l, engagement: true }));
    setErrors((e) => ({ ...e, engagement: null }));
    try {
      const data = await getMailchimpEngagement(30, { refresh });
      setEngagement(data);
      setErrors((e) => ({ ...e, engagement: errorFromResponse(data) }));
    } catch {
      setErrors((e) => ({ ...e, engagement: 'Failed to load engagement' }));
    } finally {
      setLoading((l) => ({ ...l, engagement: false }));
    }
  }, []);

  const loadCampaigns = useCallback(async (refresh = false) => {
    setLoading((l) => ({ ...l, campaigns: true }));
    setErrors((e) => ({ ...e, campaigns: null }));
    try {
      const data = await getMailchimpCampaigns(10, { refresh });
      setCampaigns(data);
      setErrors((e) => ({ ...e, campaigns: errorFromResponse(data) }));
    } catch {
      setErrors((e) => ({ ...e, campaigns: 'Failed to load campaigns' }));
    } finally {
      setLoading((l) => ({ ...l, campaigns: false }));
    }
  }, []);

  useEffect(() => {
    loadGrowth();
  }, [loadGrowth]);
  useEffect(() => {
    loadDailyGrowth();
  }, [loadDailyGrowth]);
  useEffect(() => {
    loadHwSaas();
  }, [loadHwSaas]);
  useEffect(() => {
    loadEngagement();
  }, [loadEngagement]);
  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleRefreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadGrowth(true),
        loadDailyGrowth(true),
        loadHwSaas(true),
        loadEngagement(true),
        loadCampaigns(true),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [loadGrowth, loadDailyGrowth, loadHwSaas, loadEngagement, loadCampaigns]);

  const lastUpdated = useMemo(() => {
    const stamps = [
      growth?.as_of,
      dailyGrowth?.as_of,
      hwSaas?.as_of,
      engagement?.as_of,
      campaigns?.as_of,
    ].filter(Boolean) as string[];
    if (!stamps.length) return null;
    return stamps.sort().slice(-1)[0];
  }, [growth, dailyGrowth, hwSaas, engagement, campaigns]);

  const growthChartData = useMemo(() => {
    if (!growth) return [];
    // Backend may omit `data` on its error-fallback shape; treat as empty.
    return (growth.data ?? []).map((d) => ({
      ...d,
      label: fmtMonthShort(d.month),
    }));
  }, [growth]);

  const newSubsLastMonth = useMemo(() => {
    const months = growth?.data;
    if (!months || months.length === 0) return null;
    const last = months[months.length - 1];
    return last.new_subscribers + last.new_imports;
  }, [growth]);

  const dailyChartData = useMemo(() => {
    const rows = dailyGrowth?.data ?? [];
    // Stack lost-bars below the axis so green-above / red-below gives an
    // instant visual read of growth vs churn. Cumulative net is plotted as
    // a line so you can see whether the list is net-growing across the
    // selected window.
    let cumulative = 0;
    return rows.map((d) => {
      cumulative += d.net;
      return {
        date: d.date,
        label: fmtDayShort(d.date),
        new_subscribers: d.new_subscribers,
        unsubscribed: -d.unsubscribed,
        cleaned: -d.cleaned,
        cumulative,
      };
    });
  }, [dailyGrowth]);

  const hwSaasChartData = useMemo(() => {
    if (!hwSaas) return [];
    return [
      {
        name: 'Device only',
        value: hwSaas.device_only,
        color: HW_SAAS_COLORS.device_only,
      },
      {
        name: 'SaaS only',
        value: hwSaas.saas_only,
        color: HW_SAAS_COLORS.saas_only,
      },
      { name: 'Both', value: hwSaas.both, color: HW_SAAS_COLORS.both },
      {
        name: 'Neither',
        value: hwSaas.neither,
        color: HW_SAAS_COLORS.neither,
      },
    ].filter((d) => d.value > 0);
  }, [hwSaas]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Email Marketing</h1>
          <p className={styles.subtitle}>
            Mailchimp daily activity, longer-term growth, hardware-vs-SaaS
            intent, and recent campaigns.
          </p>
        </div>
        <div className={styles.headerActions}>
          {lastUpdated && (
            <span className={styles.lastUpdated}>
              Updated {fmtDateTime(lastUpdated)}
            </span>
          )}
          <button
            type="button"
            onClick={handleRefreshAll}
            disabled={refreshing}
            className={styles.refreshButton}
          >
            <span
              className={`${styles.refreshIcon} ${refreshing ? styles.spinning : ''}`}
              aria-hidden
            >
              ↻
            </span>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtNumber(growth?.total_subscribers)}
          </div>
          <div className={styles.statLabel}>Total subscribers</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{fmtNumber(newSubsLastMonth)}</div>
          <div className={styles.statLabel}>New (last month)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtPercent(engagement?.avg_open_rate)}
          </div>
          <div className={styles.statLabel}>Avg open rate (30d)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtPercent(engagement?.avg_click_rate)}
          </div>
          <div className={styles.statLabel}>Avg click rate (30d)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtNumber(engagement?.unsubscribes)}
          </div>
          <div className={styles.statLabel}>Unsubscribes (30d)</div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Daily activity</h2>
          <div className={styles.windowPills}>
            {DAILY_WINDOWS.map((w) => (
              <button
                key={w.days}
                type="button"
                onClick={() => setDailyDays(w.days)}
                className={`${styles.pill} ${dailyDays === w.days ? styles.pillActive : ''}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        {errors.dailyGrowth ? (
          <div className={styles.error}>{errors.dailyGrowth}</div>
        ) : loading.dailyGrowth && !dailyGrowth ? (
          <div className={styles.loading}>Loading daily activity…</div>
        ) : !dailyGrowth || dailyChartData.length === 0 ? (
          <div className={styles.empty}>No activity in this window.</div>
        ) : (
          <>
            <div className={styles.subStat}>
              <strong>
                {fmtNumber(dailyGrowth.totals.new_subscribers)}
              </strong>{' '}
              new ·{' '}
              <strong>
                {fmtNumber(
                  dailyGrowth.totals.unsubscribed + dailyGrowth.totals.cleaned
                )}
              </strong>{' '}
              left ·{' '}
              <strong>{fmtSigned(dailyGrowth.totals.net)}</strong> net over{' '}
              {dailyGrowth.days}d
            </div>
            <div className={styles.chartWrap}>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart
                  data={dailyChartData}
                  stackOffset="sign"
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.06)"
                  />
                  <XAxis
                    dataKey="label"
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    interval={Math.max(
                      Math.floor(dailyChartData.length / 12) - 1,
                      0
                    )}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    width={50}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#9ca3af"
                    tick={{ fontSize: 11 }}
                    width={60}
                  />
                  <Tooltip
                    contentStyle={TOOLTIP_STYLE}
                    labelStyle={{ color: '#e5e7eb' }}
                    formatter={(value, name) => {
                      // Loss bars are stored as negatives so the chart renders
                      // them below the axis; tooltips should still show the
                      // positive count the user expects.
                      const n =
                        typeof value === 'number' ? value : Number(value);
                      if (!Number.isFinite(n)) {
                        return [String(value), name as string];
                      }
                      const isLoss =
                        name === 'Unsubscribed' || name === 'Cleaned';
                      const display = (isLoss ? Math.abs(n) : n).toLocaleString();
                      return [display, name as string];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '0.8125rem' }} />
                  <Bar
                    yAxisId="left"
                    dataKey="new_subscribers"
                    name="New subscribers"
                    stackId="activity"
                    fill="#22c55e"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="unsubscribed"
                    name="Unsubscribed"
                    stackId="activity"
                    fill="#ef4444"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="cleaned"
                    name="Cleaned"
                    stackId="activity"
                    fill="#f59e0b"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cumulative"
                    name="Cumulative net"
                    stroke="#60a5fa"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>List growth (monthly)</h2>
          <div className={styles.windowPills}>
            {GROWTH_WINDOWS.map((w) => (
              <button
                key={w.months}
                type="button"
                onClick={() => setGrowthMonths(w.months)}
                className={`${styles.pill} ${growthMonths === w.months ? styles.pillActive : ''}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>
        {errors.growth ? (
          <div className={styles.error}>{errors.growth}</div>
        ) : loading.growth && !growth ? (
          <div className={styles.loading}>Loading growth…</div>
        ) : growthChartData.length === 0 ? (
          <div className={styles.empty}>No growth data yet.</div>
        ) : (
          <div className={styles.chartWrap}>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={growthChartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.06)"
                />
                <XAxis
                  dataKey="label"
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                  width={50}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#9ca3af"
                  tick={{ fontSize: 11 }}
                  width={60}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  labelStyle={{ color: '#e5e7eb' }}
                />
                <Legend wrapperStyle={{ fontSize: '0.8125rem' }} />
                <Bar
                  yAxisId="left"
                  dataKey="new_subscribers"
                  name="New subscribers"
                  fill="#3b82f6"
                />
                <Bar
                  yAxisId="left"
                  dataKey="unsubscribed"
                  name="Unsubscribed"
                  fill="#ef4444"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="total_subscribers"
                  name="Total subscribers"
                  stroke="#22c55e"
                  strokeWidth={2}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Hardware vs SaaS intent</h2>
        </div>
        {errors.hwSaas ? (
          <div className={styles.error}>{errors.hwSaas}</div>
        ) : loading.hwSaas && !hwSaas ? (
          <div className={styles.loading}>Loading split…</div>
        ) : !hwSaas ? (
          <div className={styles.empty}>No data.</div>
        ) : (
          <>
            <div className={styles.subStat}>
              Across <strong>{fmtNumber(hwSaas.total_active)}</strong> active
              subscribers
            </div>
            <div className={styles.chartWrap}>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={hwSaasChartData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {hwSaasChartData.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={entry.color}
                        stroke="rgba(0,0,0,0.4)"
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className={styles.legend}>
              {hwSaasChartData.map((d) => (
                <div key={d.name} className={styles.legendRow}>
                  <span
                    className={styles.legendSwatch}
                    style={{ background: d.color }}
                  />
                  <span className={styles.legendLabel}>{d.name}</span>
                  <span className={styles.legendValue}>
                    {fmtNumber(d.value)}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Recent campaigns</h2>
        {errors.campaigns ? (
          <div className={styles.error}>{errors.campaigns}</div>
        ) : loading.campaigns && !campaigns ? (
          <div className={styles.loading}>Loading campaigns…</div>
        ) : !campaigns?.campaigns?.length ? (
          <div className={styles.empty}>No campaigns sent recently.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Sent</th>
                  <th className={styles.tableNum}>Recipients</th>
                  <th className={styles.tableNum}>Open</th>
                  <th className={styles.tableNum}>Click</th>
                  <th className={styles.tableNum}>Unsubs</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {campaigns.campaigns.map((c) => (
                  <tr key={c.id}>
                    <td className={styles.subjectCell}>
                      {c.subject_line || c.title || c.id}
                    </td>
                    <td>{fmtDate(c.send_time)}</td>
                    <td className={styles.tableNum}>
                      {fmtNumber(c.emails_sent)}
                    </td>
                    <td className={styles.tableNum}>
                      {fmtPercent(c.open_rate)}
                    </td>
                    <td className={styles.tableNum}>
                      {fmtPercent(c.click_rate)}
                    </td>
                    <td className={styles.tableNum}>
                      {fmtNumber(c.unsubscribed)}
                    </td>
                    <td>
                      {c.report_url && (
                        <a
                          href={c.report_url}
                          target="_blank"
                          rel="noreferrer"
                          className={styles.link}
                        >
                          Mailchimp ↗
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.notes}>
        <h3 className={styles.notesTitle}>Notes & gaps</h3>
        <ul>
          <li>
            <strong>Customer Journeys aren't visible here.</strong> Mailchimp's
            Marketing API doesn't expose journey structure or per-step
            performance. To get per-email metrics inside this dashboard, build
            drip flows as Classic Automations rather than Customer Journeys.
          </li>
          <li>
            <strong>Daily activity has a slight lag.</strong> Mailchimp counts
            members against the day their status changed in its system, which
            can lag a few hours behind the actual user action. Today's bar
            may continue filling in for several hours after midnight UTC.
          </li>
          <li>
            <strong>Monthly bars use server-derived deltas.</strong>{' '}
            Mailchimp's growth-history endpoint reports cumulative state
            counts only (the per-month <code>optins</code> /{' '}
            <code>imports</code> fields were deprecated in 2020). Monthly
            new-subscriber counts are computed here from month-over-month
            deltas in subscribed / unsubscribed / cleaned / deleted totals.
          </li>
        </ul>
      </div>
    </div>
  );
}
